import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@environment';

import { H5PParams, TranslationMap, mergeTranslations } from './walker';
import { H5pSemanticsProviderService } from './h5p-semantics-provider.service';
import {
  TrainingTranslationActivation,
  isLanguageAvailableToLearners,
} from '@shared/training-translations/training-availability.util';

const TRANSLATIONS_API_PATH = '/api/v1/course_activity_translations';

/**
 * Structural on purpose: importing the Course model here would cycle through
 * `course-activity.model.ts`. `trainingTranslations` must be in the payload.
 */
export interface RunnerTranslationsCourse {
  readonly id: string;
  readonly language: string;
  readonly translationLanguages?: readonly string[] | null;
  readonly trainingTranslations?:
    | readonly TrainingTranslationActivation[]
    | null;
}

/** One JSON:API row of the batch endpoint (foreign JSON, snake_case). */
interface TranslationRowResource {
  readonly attributes?: {
    readonly language?: string;
    readonly data?: TranslationMap;
    readonly course_activity_id?: number | string;
  };
}

interface TranslationRowsBody {
  readonly data?: readonly TranslationRowResource[];
}

/**
 * Runner translation merge: one cached batch request per course+language,
 * joined at render time. With no translations it is a STRICT no-op.
 */
@Injectable({
  providedIn: 'root',
})
export class H5pRunnerTranslationsService {
  private readonly api = inject(ApiService);
  private readonly semanticsProvider = inject(H5pSemanticsProviderService);

  /**
   * One entry per `<courseId>:<language>` batch already loaded (or in
   * flight) this session. The stored promise never rejects.
   */
  private readonly batches = new Map<string, Promise<void>>();
  /** `<activityId>:<language>` -> stored translation map (non-empty rows). */
  private readonly mapsByActivity = new Map<string, TranslationMap>();

  /**
   * Whether a member on `language` sees translations at all. When false the
   * runner skips even the batch lookup, so learners get SOURCE content.
   */
  needsTranslations(
    course: RunnerTranslationsCourse,
    language: string,
  ): boolean {
    return (
      !!language &&
      language !== course.language &&
      isLanguageAvailableToLearners(course, language)
    );
  }

  /**
   * Batch-load a course visit's rows: ONE request per course+language, reused
   * on repeat calls. `language` is explicit so a switch can re-preload.
   */
  preload(
    course: RunnerTranslationsCourse,
    activityIds: readonly string[],
    language: string,
  ): Promise<void> {
    if (!activityIds.length || !this.needsTranslations(course, language)) {
      return Promise.resolve();
    }
    const key = `${course.id}:${language}`;
    const existing = this.batches.get(key);
    if (existing) {
      return existing;
    }
    const batch = this.fetchBatch(activityIds, language);
    this.batches.set(key, batch);
    return batch;
  }

  /** Cached translation map of one activity+language (empty rows excluded). */
  mapFor(activityId: string, language: string): TranslationMap | undefined {
    return this.mapsByActivity.get(`${activityId}:${language}`);
  }

  /**
   * Join source content with the cached map, awaiting any in-flight batch.
   * Never throws, never mutates: no map or any failure returns `content`.
   */
  async translateContent(
    activityId: string,
    library: string,
    content: H5PParams,
    language: string,
  ): Promise<H5PParams> {
    if (!language) {
      return content;
    }
    await this.whenBatchesSettled();
    const map = this.mapFor(activityId, language);
    if (!map || Object.keys(map).length === 0) {
      return content;
    }
    try {
      const semanticsRecord = await firstValueFrom(
        this.semanticsProvider.prefetch(library, content),
      );
      const semantics = semanticsRecord[library];
      if (!semantics) {
        return content;
      }
      return mergeTranslations(semantics, content, map, {
        resolveSemantics: semanticsRecord,
        language,
      });
    } catch {
      return content;
    }
  }

  /** Drop all cached batches/maps (e.g. tests or a future manual refresh). */
  clear(): void {
    this.batches.clear();
    this.mapsByActivity.clear();
  }

  private async whenBatchesSettled(): Promise<void> {
    if (!this.batches.size) {
      return;
    }
    try {
      await Promise.all(this.batches.values());
    } catch {
      // Batches never reject — belt and braces only.
    }
  }

  private async fetchBatch(
    activityIds: readonly string[],
    language: string,
  ): Promise<void> {
    try {
      const filters =
        `filter[course_activity_id]=${activityIds.join(',')}` +
        `&filter[language]=${encodeURIComponent(language)}`;
      const response = await firstValueFrom(
        this.api.get(
          environment.backendBaseUrl,
          `${TRANSLATIONS_API_PATH}?${filters}`,
        ),
      );
      this.storeRows(
        (response?.body as TranslationRowsBody | undefined)?.data ?? [],
        language,
      );
    } catch {
      // Translations must never break the runner — fall back to source.
    }
  }

  private storeRows(
    rows: readonly TranslationRowResource[],
    language: string,
  ): void {
    for (const row of rows) {
      const attributes = row?.attributes;
      const activityId = attributes?.course_activity_id;
      const data = attributes?.data;
      if (
        activityId === undefined ||
        activityId === null ||
        !data ||
        typeof data !== 'object' ||
        Object.keys(data).length === 0
      ) {
        continue;
      }
      this.mapsByActivity.set(
        `${activityId}:${attributes?.language ?? language}`,
        data,
      );
    }
  }
}
